import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PURITIES = ["24K", "22K", "21K", "18K", "14K"];

/** Today's spot from several sources side by side, with the shop's margin applied.
    Nothing is ever set automatically — "Use" only fills the rate box. */
export const LiveSpotPanel = ({ branch, onUse }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [margins, setMargins] = useState({});
  const [abc, setAbc] = useState("");

  const load = (refresh = false) => {
    setBusy(true);
    api
      .get("/gold-rates/live", { params: { branch, refresh } })
      .then(({ data }) => {
        setData(data);
        setMargins(data.margins || {});
      })
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch]);

  const saveMargins = async () => {
    try {
      await api.put("/gold-rates/margins", { branch, margins });
      toast.success("Margin saved");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const saveAbc = async () => {
    const value = Number(abc);
    if (!value) return toast.error("Type the figure from ABC Bullion's page");
    try {
      await api.put("/gold-rates/abc-spot", {
        branch,
        ...(value > 1000 ? { per_oz: value } : { per_gram: value }),
      });
      toast.success("ABC Bullion spot recorded for today");
      setAbc("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const setMargin = (purity, field, value) =>
    setMargins({
      ...margins,
      [purity]: { ...(margins[purity] || { pct: 0, fixed: 0 }), [field]: value },
    });

  const sources = data?.sources || [];

  return (
    <div className="border border-border" data-testid="live-spot-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-[#0A0A0A] px-4 py-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-white">
          Live market rates (per gram, AUD)
        </h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 rounded-none border-[#C5A059] bg-transparent text-[#C5A059] hover:bg-[#C5A059] hover:text-[#0A0A0A]"
            onClick={() => window.open(data?.abc_url, "_blank", "noreferrer")}
            data-testid="open-abc-bullion-button"
          >
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" /> Open ABC Bullion
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 rounded-none border-[#C5A059] bg-transparent text-[#C5A059] hover:bg-[#C5A059] hover:text-[#0A0A0A]"
            disabled={busy}
            onClick={() => load(true)}
            data-testid="refresh-spot-button"
          >
            <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="border-b border-border bg-[#FBF9F5] px-4 py-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider">
              ABC Bullion spot (AUD per ounce, or per gram)
            </Label>
            <Input
              type="number"
              step="0.01"
              className="mt-1 h-9 w-[220px] rounded-none bg-white"
              placeholder="e.g. 5945.30"
              value={abc}
              onChange={(e) => setAbc(e.target.value)}
              data-testid="abc-spot-input"
            />
          </div>
          <Button
            variant="outline"
            className="h-9 rounded-none"
            onClick={saveAbc}
            data-testid="abc-spot-save-button"
          >
            Record today's ABC spot
          </Button>
          <p className="max-w-[460px] text-xs text-muted-foreground">
            ABC Bullion block automated reads, so open their page, read the gold spot and
            type it here once each morning — the portal does the per-gram and per-purity
            maths. Everything else below updates itself.
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm" data-testid="spot-table">
          <thead className="bg-[#F8F9FA]">
            <tr className="border-b border-border">
              <th className="px-3 py-2.5 text-left font-semibold">Source</th>
              <th className="px-3 py-2.5 text-right font-semibold">24K spot /g</th>
              {PURITIES.map((p) => (
                <th key={p} className="px-3 py-2.5 text-right font-semibold">
                  {p}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sources.map((s) => (
              <tr key={s.id} className="hover:bg-[#FBF8F2]" data-testid={`spot-row-${s.id}`}>
                <td className="px-3 py-2.5">
                  <div className="font-semibold">{s.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {s.ok
                      ? s.manual
                        ? `${money(s.aud_per_oz)}/oz · ${s.stale ? "entered on an earlier day" : "entered today"} by ${s.entered_by}`
                        : `${money(s.aud_per_oz)}/oz`
                      : s.error}
                  </div>
                </td>
                <td className="tabular px-3 py-2.5 text-right">
                  {s.ok ? money(s.aud_per_gram) : "—"}
                </td>
                {PURITIES.map((p) => (
                  <td key={p} className="px-3 py-2.5 text-right">
                    {s.ok ? (
                      <button
                        type="button"
                        onClick={() => onUse(p, s.suggested[p])}
                        className="tabular font-semibold underline decoration-dotted hover:text-[#B38F4E]"
                        title={`Spot ${money(s.per_gram[p])} + your margin — click to fill the ${p} box`}
                        data-testid={`use-spot-${s.id}-${p}`}
                      >
                        {money(s.suggested[p])}
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                ))}
              </tr>
            ))}
            {!sources.length && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                  No sources could be read just now.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="border-t border-border px-4 py-4">
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Your margin over spot — a percentage, a flat amount per gram, or both
        </h3>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          {PURITIES.map((p) => (
            <div key={p} className="w-[132px]">
              <Label className="text-[11px] uppercase tracking-wider">{p}</Label>
              <div className="mt-1 flex gap-1">
                <Input
                  type="number"
                  step="0.1"
                  className="h-9 rounded-none px-1.5 text-right"
                  placeholder="%"
                  value={margins[p]?.pct ?? ""}
                  onChange={(e) => setMargin(p, "pct", e.target.value)}
                  data-testid={`margin-pct-${p}`}
                />
                <Input
                  type="number"
                  step="0.01"
                  className="h-9 rounded-none px-1.5 text-right"
                  placeholder="$"
                  value={margins[p]?.fixed ?? ""}
                  onChange={(e) => setMargin(p, "fixed", e.target.value)}
                  data-testid={`margin-fixed-${p}`}
                />
              </div>
            </div>
          ))}
          <Button
            variant="outline"
            className="h-9 rounded-none"
            onClick={saveMargins}
            data-testid="save-margins-button"
          >
            Save margin
          </Button>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Click any figure above to drop it into that purity's box below — it is never set
          for you. {data?.note}
        </p>
      </div>
    </div>
  );
};
